import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Logo } from "./Header";
import { track } from "../lib/track";

const CITIES = [
  ["/sell/san-jose", "Sell in San Jose"],
  ["/sell/cupertino", "Sell in Cupertino"],
  ["/sell/palo-alto", "Sell in Palo Alto"],
  ["/sell/los-gatos", "Sell in Los Gatos"],
  ["/sell/san-mateo", "Sell in San Mateo"],
  ["/sell/redwood-city", "Sell in Redwood City"],
  ["/sell/burlingame", "Sell in Burlingame"],
  ["/sell/menlo-park", "Sell in Menlo Park"],
  ["/sell/atherton", "Sell in Atherton"],
  ["/sell/hillsborough", "Sell in Hillsborough"],
  ["/sell/woodside", "Sell in Woodside"],
];

export default function Footer() {
  return (
    <footer className="bg-brand-dark text-brand-cream/80 mt-24" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo light />
          <p className="mt-5 max-w-md font-serif text-2xl leading-snug text-brand-cream">
            It's not just what you sell for—it's what you keep.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-brand-cream/60 max-w-md">
            Full-service real estate representation for a 1% listing fee, serving Santa Clara &amp; San Mateo counties.
          </p>
        </div>
        <div>
          <h4 className="text-brand-gold text-xs uppercase tracking-[0.2em] font-semibold">Brokerage</h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li>Louis Fong, Broker</li>
            <li className="text-brand-cream/60"><a href="https://equitashomes.com" className="hover:text-brand-gold" data-testid="footer-brokerage-dre">Equitas Homes · CA DRE #01151598</a></li>
            <li><a href="tel:+14085611321" onClick={() => track("contact_click", { channel: "phone" })} className="hover:text-brand-gold">(408) 561-1321</a></li>
            <li><a href="mailto:louis@equitashomes.com" onClick={() => track("contact_click", { channel: "email" })} className="hover:text-brand-gold">louis@equitashomes.com</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-brand-gold text-xs uppercase tracking-[0.2em] font-semibold">Links</h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href="https://equitashomes.com" className="hover:text-brand-gold">Equitas Homes</a></li>
            <li><Link to="/" className="hover:text-brand-gold">Home Value Tool</Link></li>
            <li><Link to="/how-it-works" className="hover:text-brand-gold" data-testid="footer-how-it-works-link">How It Works</Link></li>
            <li><Link to="/selling-costs" className="hover:text-brand-gold" data-testid="footer-selling-costs-link">Selling Costs</Link></li>
            <li><Link to="/services" className="hover:text-brand-gold" data-testid="footer-services-link">What We Do</Link></li>
            <li><Link to="/2-percent-direct-sale" className="hover:text-brand-gold" data-testid="footer-direct-sale-link">2% Direct Sale</Link></li>
            <li><Link to="/about" className="hover:text-brand-gold" data-testid="footer-about-link">About</Link></li>
            <li><Link to="/contact" onClick={() => track("contact_click", { channel: "footer-contact" })} className="hover:text-brand-gold" data-testid="footer-contact-link">Contact</Link></li>
            <li><Link to="/blog" className="hover:text-brand-gold" data-testid="footer-blog-link">Blog</Link></li>
          </ul>

          <details className="group mt-3" data-testid="footer-cities-accordion">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 text-sm text-brand-cream/80 hover:text-brand-gold transition-colors" data-testid="footer-cities-summary">
              Cities &amp; Areas We Serve
              <ChevronDown className="h-4 w-4 transition-transform duration-300 group-open:rotate-180" aria-hidden="true" />
            </summary>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 text-sm" data-testid="footer-cities-list">
              {CITIES.map(([to, label]) => (
                <li key={to}><Link to={to} className="hover:text-brand-gold" data-testid={`footer-sell-${to.split("/").pop()}`}>{label}</Link></li>
              ))}
            </ul>
          </details>

          <ul className="mt-4 space-y-2 text-sm border-t border-brand-cream/10 pt-4">
            <li><Link to="/privacy" className="hover:text-brand-gold">Privacy Notice</Link></li>
            <li><Link to="/terms" className="hover:text-brand-gold" data-testid="footer-terms-link">Terms of Service</Link></li>
            <li><Link to="/admin" className="hover:text-brand-gold" data-testid="footer-admin-link">Admin</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-brand-cream/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 text-xs text-brand-cream/50 space-y-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-1" data-testid="footer-eho">
            <span className="inline-flex items-center gap-2 text-brand-cream/70">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 11.2 12 4l9 7.2" /><path d="M5.2 10v9h13.6v-9" /><path d="M9.3 13.4h5.4M9.3 15.9h5.4" />
              </svg>
              Equal Housing Opportunity.
            </span>
            <a href="https://equitashomes.com" className="underline hover:text-brand-gold" data-testid="footer-dre">Equitas Homes · CA DRE #01151598</a>
          </div>
          <p>All brokerage commissions and fees are negotiable and are not set by law. Buyer representation and dual agency are subject to applicable agreements and disclosure requirements.</p>
          <p>Home value estimates are automated and for informational purposes only — not an appraisal or a guaranteed sale price. © {new Date().getFullYear()} Equitas Homes.</p>
          <p data-testid="bridge-mls-disclaimer">Valuation estimates are generated using automated valuation models (AVM) and do not constitute an official appraisal. Market data provided via Bridge Interactive. Subject to terms and conditions.</p>
          <p data-testid="zillow-footer-disclaimer">© Zillow, Inc., 2006–2026. Use is subject to <a href="https://www.zillow.com/corp/Terms.htm" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-gold">Terms of Use</a></p>
        </div>
      </div>
    </footer>
  );
}
